import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Clock, CheckCircle2, AlertCircle, XCircle, Calendar, Building2, ExternalLink, Eye } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';

const TalentApplications = () => {
  const { profile } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);

  usePageMeta({
    title: 'My Applications | Talent Collective',
    description: 'Track the status of your project applications.'
  });

  // Format date consistently as "15 Feb 2026"
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredApps = filter === 'all' 
    ? applications 
    : applications.filter(app => app.status === filter);

  const statusConfig = {
    submitted: { 
      label: 'Submitted', 
      icon: Clock, 
      color: 'badge-neutral',
      description: 'Your application has been received.'
    },
    under_review: { 
      label: 'Under Review', 
      icon: AlertCircle, 
      color: 'badge-warning',
      description: 'The organization is reviewing your application.'
    },
    shortlisted: { 
      label: 'Shortlisted', 
      icon: CheckCircle2, 
      color: 'badge-primary',
      description: 'Congratulations! You have been shortlisted.'
    },
    intro_scheduled: { 
      label: 'Intro Scheduled', 
      icon: Calendar, 
      color: 'badge-success',
      description: 'An introduction has been scheduled.'
    },
    not_selected: { 
      label: 'Not Selected', 
      icon: XCircle, 
      color: 'badge-error',
      description: 'Unfortunately, you were not selected for this project.'
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="talent-applications-page">
      <Header />

      <div className="container-custom py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="heading-2 mb-2">My Applications</h1>
            <p className="text-slate-600">Track the status of your project applications.</p>
          </div>

          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 mt-4 md:mt-0" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Applications</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="shortlisted">Shortlisted</SelectItem>
              <SelectItem value="intro_scheduled">Intro Scheduled</SelectItem>
              <SelectItem value="not_selected">Not Selected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="h-5 bg-slate-200 rounded w-1/3 mb-2"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/4 mb-4"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                  <div className="h-6 bg-slate-200 rounded w-24"></div>
                </div>
              </Card>
            ))}
          </div>
        ) : filteredApps.length > 0 ? (
          <div className="space-y-4">
            {filteredApps.map((app) => {
              const status = statusConfig[app.status];
              const StatusIcon = status?.icon || Clock;
              
              return (
                <Card key={app.id} className="p-6" data-testid={`application-${app.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h3 className="heading-4 mb-1">{app.project_title}</h3>
                          <p className="text-slate-500 flex items-center text-sm mb-3">
                            <Building2 className="h-4 w-4 mr-1" />
                            {app.company_name}
                          </p>
                          
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              Applied {formatDate(app.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                      <span className={`${status?.color} flex items-center`}>
                        <StatusIcon className="h-3.5 w-3.5 mr-1" />
                        {status?.label}
                      </span>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedApp(app)}
                          data-testid={`view-application-${app.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                        <Link to={`/projects/${app.project_id}`}>
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View Project
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="heading-4 mb-2">No applications yet.</h3>
            <p className="text-slate-500 mb-6">
              {filter !== 'all' 
                ? `No applications with "${filter.replace(/_/g, ' ')}" status.`
                : 'Start exploring projects and apply to ones that match your expertise.'}
            </p>
            <Link to="/projects">
              <Button className="btn-primary" data-testid="browse-projects-btn">
                Browse Opportunities
              </Button>
            </Link>
          </Card>
        )}
      </div>

      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="application-detail-modal">
          <DialogHeader>
            <DialogTitle>{selectedApp?.project_title}</DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6">
              <div>
                <p className="text-sm text-slate-500 mb-1">Organization</p>
                <p className="font-medium">{selectedApp.company_name}</p>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-1">Status</p>
                <span className={`${statusConfig[selectedApp.status]?.color}`}>
                  {statusConfig[selectedApp.status]?.label}
                </span>
                <p className="text-sm text-slate-500 mt-1">
                  {statusConfig[selectedApp.status]?.description}
                </p>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-1">Applied On</p>
                <p className="font-medium">
                  {formatDate(selectedApp.created_at)}
                </p>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Your Pitch</p>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedApp.pitch_note}</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Link to={`/projects/${selectedApp.project_id}`}>
                  <Button className="w-full">
                    View Full Project Details
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default TalentApplications;
